import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaUserTie, FaEnvelope, FaBuilding, FaSpinner, FaGavel, FaPercentage, FaHeadset } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import Agent from '../../models/Agent';
import { toast } from 'react-toastify';
import Parse, { User } from 'parse';
import UserModel from '../../models/User';

const AgentDetailsContainer = styled.div`
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  margin: 0 auto;
`;

const AgentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #1a2a6c 0%, #2a3a7c 100%);
  border-radius: 12px;
  color: white;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 30%;
    background: linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 100%);
    transform: skewX(-15deg) translateX(50%);
  }
`;

const AgentAvatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 16px;
  background: ${props => props.hasImage ? 'none' : 'rgba(255,255,255,0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 2rem;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  border: 3px solid rgba(255,255,255,0.2);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    font-size: 48px;
    color: rgba(255,255,255,0.9);
  }
`;

const AgentInfo = styled.div`
  flex: 1;
  
  h2 {
    font-size: 2rem;
    margin: 0 0 0.5rem;
    color: white;
    font-weight: 600;
  }

  p {
    color: rgba(255,255,255,0.8);
    font-size: 1.1rem;
    margin: 0;
  }
`;

const AgentName = styled.h2`
  margin: 0 0 5px 0;
  color: #333;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const TabsContainer = styled.div`
  margin-top: 2rem;
`;

const TabList = styled.div`
  display: flex;
  gap: 1rem;
  border-bottom: 1px solid #eee;
  margin-bottom: 2rem;
`;

const TabButton = styled.button`
  padding: 0.8rem 1.5rem;
  background: none;
  border: none;
  color: ${props => props.active ? '#1a2a6c' : '#666'};
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: 1rem;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.active ? '#1a2a6c' : 'transparent'};
    transition: all 0.3s ease;
  }

  &:hover {
    color: #1a2a6c;
  }
`;

const TabContent = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const AgentStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const StatCard = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid #eee;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  }
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p {
    margin: 0;
    font-size: 1.8rem;
    color: #1a2a6c;
    font-weight: 600;
  }
`;

const InfoSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  border-radius: 12px;
  background: #f8f9fa;
  border: 1px solid #eee;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  }

  h3 {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    margin-bottom: 1rem;
    color: #1a2a6c;
    font-size: 1.2rem;

    svg {
      color: #2a3a7c;
    }
  }

  p {
    color: #444;
    line-height: 1.8;
    white-space: pre-line;
    font-size: 1rem;
  }
`;

const ContactInfo = styled(InfoSection)`
  margin-top: 20px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  
  svg {
    margin-right: 10px;
    color: #666;
  }
  
  span {
    color: #333;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #666;
  
  svg {
    animation: spin 1s linear infinite;
    font-size: 2rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #dc3545;
  padding: 2rem;
`;

const AgentDetails = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchAgentData = async () => {
      try {
        // Get the agent pointer from the current user
        const agencyId = currentUser.get('agencyId');
        if (!agencyId) {
          throw new Error('No agency found for this user');
        }

        // Fetch the agent details
        const query = new Parse.Query(Agent);
        query.include('author');
        const agent = await query.get(agencyId);

        if (!agent) {
          throw new Error('Agent not found');
        }

        // Get the agent's author (user) details
        const author = agent.get('author');
        if (!author) {
          throw new Error('Author not found');
        } else {
          console.log('Author found:', author);
        }

        setAgentData({
          agencyName: agent.get(Agent.keys.NAME) ?? author.get(UserModel.keys.FULL_NAME),
          agencyLogo: agent.get(Agent.keys.LOGO)?.url() || author.get(UserModel.keys.AVATAR_FILE)?.url(),
          description: agent.get(Agent.keys.DESCRIPTION),
          uid: agent.get(Agent.keys.UID),
          email: agent.get(Agent.keys.EMAIL),
          stats: {
            totalHosts: agent.get(Agent.keys.HOST_IDS) ? agent.get(Agent.keys.HOST_IDS).length : 0,
            activeHosts: agent.get(Agent.keys.HOST_IDS) ? agent.get(Agent.keys.HOST_IDS).length : 0
          },
          rules: agent.get(Agent.keys.RULES) || t('agency.join.details.noRules'),
          commissions: agent.get(Agent.keys.COMMISSIONS) || t('agency.join.details.noCommissions'),
          support: agent.get(Agent.keys.SUPPORT) || t('agency.join.details.noSupport')
        });
      } catch (err) {
        console.error('Error fetching agent details:', err);
        setError(err.message);
        toast.error(t('agency.join.fetchError'));
      } finally {
        setLoading(false);
      }
    };

    fetchAgentData();
  }, [currentUser, t]);

  if (loading) {
    return (
      <AgentDetailsContainer>
        <LoadingSpinner>
          <FaSpinner />
        </LoadingSpinner>
      </AgentDetailsContainer>
    );
  }

  if (error) {
    return (
      <AgentDetailsContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </AgentDetailsContainer>
    );
  }

  if (!agentData) {
    return (
      <AgentDetailsContainer>
        <ErrorMessage>{t('agency.join.noData')}</ErrorMessage>
      </AgentDetailsContainer>
    );
  }

  return (
    <AgentDetailsContainer>
      <AgentHeader>
        <AgentAvatar hasImage={!!agentData.agencyLogo}>
          {agentData.agencyLogo ? (
            <img src={agentData.agencyLogo} alt={agentData.agencyName} />
          ) : (
            <FaUserTie />
          )}
        </AgentAvatar>
        <AgentInfo>
          <h2>{agentData.agencyName}</h2>
          <p>{agentData.description}</p>
        </AgentInfo>
      </AgentHeader>

      <TabsContainer>
        <TabList>
          <TabButton 
            active={activeTab === 'overview'} 
            onClick={() => setActiveTab('overview')}
          >
            {t('agency.join.details.tabs.overview')}
          </TabButton>
          <TabButton 
            active={activeTab === 'rules'} 
            onClick={() => setActiveTab('rules')}
          >
            {t('agency.join.details.tabs.rules')}
          </TabButton>
          <TabButton 
            active={activeTab === 'commissions'} 
            onClick={() => setActiveTab('commissions')}
          >
            {t('agency.join.details.tabs.commissions')}
          </TabButton>
          <TabButton 
            active={activeTab === 'support'} 
            onClick={() => setActiveTab('support')}
          >
            {t('agency.join.details.tabs.support')}
          </TabButton>
        </TabList>

        <TabContent active={activeTab === 'overview'}>
          <StatsGrid>
            <StatCard>
              <h4>{t('dashboard.agent.totalHosts')}</h4>
              <p>{agentData.stats.totalHosts}</p>
            </StatCard>
            <StatCard>
              <h4>{t('dashboard.agent.activeHosts')}</h4>
              <p>{agentData.stats.activeHosts}</p>
            </StatCard>
          </StatsGrid>

          <InfoSection>
            <h3>
              <FaEnvelope />
              {t('agency.join.details.tabs.contactInfo')}
            </h3>
            <p>{agentData.email}</p>
            <p style={{ marginTop: '10px' }}>
              <FaBuilding style={{ marginRight: '8px' }} />
              {agentData.agencyName}
            </p>
          </InfoSection>
        </TabContent>

        <TabContent active={activeTab === 'rules'}>
          <InfoSection>
            <h3>
              <FaGavel />
              {t('agency.join.details.tabs.rules')}
            </h3>
            <p>{agentData.rules}</p>
          </InfoSection>
        </TabContent>

        <TabContent active={activeTab === 'commissions'}>
          <InfoSection>
            <h3>
              <FaPercentage />
              {t('agency.join.details.tabs.commissions')}
            </h3>
            <p>{agentData.commissions}</p>
          </InfoSection>
        </TabContent>

        <TabContent active={activeTab === 'support'}>
          <InfoSection>
            <h3>
              <FaHeadset />
              {t('agency.join.details.tabs.support')}
            </h3>
            <p>{agentData.support}</p>
          </InfoSection>
        </TabContent>
      </TabsContainer>
    </AgentDetailsContainer>
  );
};

export default AgentDetails;
